import fetcher from '../lib/fetcher'
import {NetworkTrackLabelListSchema} from '../schemas/referential.schemas'
import {
  TCreateTrackLabel,
  TCreateTrackSegment,
  TDeclareSwitchConnection,
  TNetworkTrackLabel,
  TQualifySwitchConnection,
  TSwitchConnection,
  TTrackSegment,
  TUpdateTrackLabel,
  TUpdateTrackSegment,
} from '../types/referential.type'

const base = (networkId: string) => `/api/network/${networkId}/referential`

const getTracks = async (networkId: string): Promise<TNetworkTrackLabel[]> => {
  const response = await fetcher.get<TNetworkTrackLabel[]>(
    `${base(networkId)}/tracks`,
  )
  // Parsed, not cast: the expansion panel reads `segments` unconditionally.
  return NetworkTrackLabelListSchema.parse(response.data ?? [])
}

const createTrack = async (networkId: string, track: TCreateTrackLabel) => {
  const response = await fetcher.post<TCreateTrackLabel, TNetworkTrackLabel>(
    `${base(networkId)}/tracks`,
    track,
  )
  return response.data
}

const updateTrack = async (
  networkId: string,
  id: string,
  track: TUpdateTrackLabel,
) => {
  const response = await fetcher.patch<TUpdateTrackLabel, TNetworkTrackLabel>(
    `${base(networkId)}/tracks/${id}`,
    track,
  )
  return response.data
}

const deleteTrack = async (networkId: string, id: string) => {
  const response = await fetcher.del(`${base(networkId)}/tracks/${id}`)
  return response.data
}

const createSegment = async (
  networkId: string,
  trackId: string,
  segment: TCreateTrackSegment,
) => {
  const response = await fetcher.post<TCreateTrackSegment, TTrackSegment>(
    `${base(networkId)}/tracks/${trackId}/segments`,
    segment,
  )
  return response.data
}

const updateSegment = async (
  networkId: string,
  id: string,
  segment: TUpdateTrackSegment,
) => {
  const response = await fetcher.patch<TUpdateTrackSegment, TTrackSegment>(
    `${base(networkId)}/segments/${id}`,
    segment,
  )
  return response.data
}

const deleteSegment = async (networkId: string, id: string) => {
  const response = await fetcher.del(`${base(networkId)}/segments/${id}`)
  return response.data
}

/**
 * Every switch of the network, devices and connections alike.
 *
 * NOT under `/referential`: the listing belongs to the network-level geographic
 * reads, beside geo-markers, train-stations, sub-sectors and accesses. Only the
 * writes are referential routes.
 */
const getNetworkSwitches = async (
  networkId: string,
): Promise<TSwitchConnection[]> => {
  const response = await fetcher.get<TSwitchConnection[]>(
    `/api/network/${networkId}/switches`,
  )
  return response.data ?? []
}

const declareSwitch = async (
  networkId: string,
  connection: TDeclareSwitchConnection,
) => {
  const response = await fetcher.post<TDeclareSwitchConnection, unknown>(
    `${base(networkId)}/switches`,
    connection,
  )
  return response.data
}

const qualifySwitch = async (
  networkId: string,
  id: string,
  connection: TQualifySwitchConnection,
) => {
  const response = await fetcher.patch<TQualifySwitchConnection, unknown>(
    `${base(networkId)}/switches/${id}`,
    connection,
  )
  return response.data
}

const deleteSwitchConnection = async (networkId: string, id: string) => {
  const response = await fetcher.del(`${base(networkId)}/switches/${id}`)
  return response.data
}

const ReferentialService = {
  getTracks,
  createTrack,
  updateTrack,
  deleteTrack,
  createSegment,
  updateSegment,
  deleteSegment,
  getNetworkSwitches,
  declareSwitch,
  qualifySwitch,
  deleteSwitchConnection,
}

export default ReferentialService
